import { PLAN_IDS, PLAN_LABEL, type Me, type Permission, type PlanId } from '@agents/shared';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate, type LinkProps } from '@tanstack/react-router';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { tenantSelection } from '../lib/api';
import { useServices } from '../lib/context';
import { initials } from '../lib/format';
import { useMe } from '../lib/queries';
import { THEME_OPTIONS, useTheme, type ThemePreference } from '../lib/theme';
import { Icon, type IconName } from './icons';
import { Button, ErrorState, PersonAvatar, SkeletonRows } from './ui';

const MeCtx = createContext<Me | null>(null);

export function useCurrentMe(): Me {
  const me = useContext(MeCtx);
  if (!me) throw new Error('AppLayout missing');
  return me;
}

export function useCan(): (p: Permission) => boolean {
  const me = useCurrentMe();
  const perms = new Set(me.activeTenant?.permissions ?? []);
  return (p) => perms.has(p);
}

const ROLE_LABEL: Record<string, string> = { tenant_admin: 'Administrador', tenant_member: 'Miembro' };

function switchTenant(id: string) {
  tenantSelection.set(id);
  // Full reload guarantees no cached data from the previous tenant survives.
  window.location.assign('/agentes');
}

/** Plans are cumulative levels, so the badge shows a level, never a colour (Doc. 02/03 §06). */
export function PlanBadge({ plan }: { plan: PlanId }) {
  const level = PLAN_IDS.indexOf(plan) + 1;
  return (
    <span className="plan-badge" title={`Plan contratado: ${PLAN_LABEL[plan]}`}>
      {PLAN_LABEL[plan]}
      <span className="level" aria-hidden="true">
        {'●'.repeat(level)}
        {'○'.repeat(PLAN_IDS.length - level)}
      </span>
    </span>
  );
}

function TenantSwitcher({ me }: { me: Me }) {
  const active = me.activeTenant;
  if (!active) return null;
  const roleLabel = active.role ? ROLE_LABEL[active.role] : 'Administrador de plataforma';
  const body = (
    <>
      <span className="tenant-mark" aria-hidden="true">
        {initials(active.name)}
      </span>
      <span style={{ minWidth: 0 }}>
        <strong>{active.name}</strong>
        <small>{roleLabel}</small>
      </span>
    </>
  );
  if (me.tenants.length <= 1 && !me.isPlatformAdmin) {
    return (
      <div className="switcher" data-static>
        {body}
      </div>
    );
  }
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button
          className="switcher"
          type="button"
          aria-label={`Espacio actual: ${active.name}. Cambiar de espacio`}
        >
          {body}
          <Icon name="selector" />
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className="menu" align="start" sideOffset={6}>
          <Menu.Label className="menu-label">Cambiar de espacio</Menu.Label>
          {me.tenants.map((t) => (
            <Menu.Item
              key={t.id}
              className="menu-item"
              onSelect={() => t.id !== active.id && switchTenant(t.id)}
              aria-current={t.id === active.id ? 'true' : undefined}
            >
              <span className="indicator">{t.id === active.id && <Icon name="check" />}</span>
              {t.name}
            </Menu.Item>
          ))}
          {me.isPlatformAdmin && (
            <>
              <Menu.Separator className="menu-sep" />
              <Menu.Item className="menu-item" asChild>
                <Link to="/plataforma/espacios">
                  <Icon name="spaces" />
                  Todos los espacios
                </Link>
              </Menu.Item>
            </>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

function UserMenu({ me }: { me: Me }) {
  const { auth } = useServices();
  const [theme, setTheme] = useTheme();
  const name = me.displayName ?? me.email;
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button className="user-btn" type="button" aria-label={`Cuenta de ${me.email}`}>
          <PersonAvatar name={name} />
          <span style={{ minWidth: 0 }}>
            <strong>{name}</strong>
            {me.displayName && <small>{me.email}</small>}
          </span>
          <Icon name="selector" />
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className="menu" side="top" align="start" sideOffset={6}>
          <Menu.Label className="menu-label">Tema</Menu.Label>
          <Menu.RadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemePreference)}>
            {THEME_OPTIONS.map((o) => (
              <Menu.RadioItem key={o.value} value={o.value} className="menu-item">
                <span className="indicator">
                  <Menu.ItemIndicator>
                    <Icon name="check" />
                  </Menu.ItemIndicator>
                </span>
                {o.label}
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
          <Menu.Separator className="menu-sep" />
          <Menu.Item className="menu-item" onSelect={() => void auth.signOut()}>
            <Icon name="logout" />
            Cerrar sesión
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

function NavLink({ to, icon, children }: { to: LinkProps['to']; icon: IconName; children: string }) {
  return (
    <Link to={to} className="nav-link">
      <Icon name={icon} />
      {children}
    </Link>
  );
}

function Sidebar({ me, open, environment }: { me: Me; open: boolean; environment: string }) {
  const can = (p: Permission) => me.activeTenant?.permissions.includes(p) ?? false;
  return (
    <aside className="sidebar" id="main-nav" data-open={open} aria-label="Navegación principal">
      <Link to="/" className="wordmark" aria-label="Agents by Stratika, inicio">
        <strong>
          Agents<span>.</span>
        </strong>
        <small>by Stratika</small>
      </Link>
      <div className="stack" style={{ gap: 8 }}>
        <TenantSwitcher me={me} />
        {me.activeTenant && (
          <div className="plan-row">
            <span className="muted" style={{ fontSize: 12 }}>
              Plan
            </span>
            <PlanBadge plan={me.activeTenant.plan} />
          </div>
        )}
      </div>
      <nav className="nav">
        {me.activeTenant && (
          <div className="nav-group">
            <h2>Operación</h2>
            <NavLink to="/agentes" icon="node">
              Agentes
            </NavLink>
            <NavLink to="/ejecuciones" icon="activity">
              Ejecuciones
            </NavLink>
            {can('knowledge.read') && (
              <NavLink to="/conocimiento" icon="book">
                Conocimiento
              </NavLink>
            )}
            <NavLink to="/herramientas" icon="wrench">
              Herramientas
            </NavLink>
          </div>
        )}
        {can('member.read') && (
          <div className="nav-group">
            <h2>Espacio</h2>
            <NavLink to="/equipo" icon="users">
              Equipo
            </NavLink>
          </div>
        )}
        {me.isPlatformAdmin && (
          <div className="nav-group">
            <h2>Plataforma</h2>
            <NavLink to="/plataforma/espacios" icon="spaces">
              Espacios
            </NavLink>
          </div>
        )}
      </nav>
      <div className="sidebar-foot">
        {environment !== 'production' && (
          <span className="env-badge" title="Ambiente">
            {environment}
          </span>
        )}
        <UserMenu me={me} />
      </div>
    </aside>
  );
}

function NoAccess({ me }: { me: Me }) {
  const { auth } = useServices();
  return (
    <main className="auth" data-single>
      <div className="auth-card">
        <div className="wordmark" style={{ padding: 0 }}>
          <strong>
            Agents<span>.</span>
          </strong>
          <small>by Stratika</small>
        </div>
        <div>
          <h1>Aún no tienes acceso a un espacio</h1>
          <p>
            Iniciaste sesión como <strong>{me.email}</strong>, pero nadie te ha agregado a un espacio de
            trabajo. Pide a un administrador de tu organización que te invite con este correo.
          </p>
        </div>
        <Button icon="logout" onClick={() => void auth.signOut()}>
          Cerrar sesión
        </Button>
      </div>
    </main>
  );
}

/** Shell placeholder while /me loads, so the layout does not jump once it arrives. */
function ShellSkeleton() {
  return (
    <div className="shell" aria-busy="true" aria-label="Cargando">
      <aside className="sidebar" aria-hidden="true">
        <div className="skeleton" style={{ width: 96, height: 20, margin: '4px 10px' }} />
        <div className="skeleton" style={{ height: 46, borderRadius: 8 }} />
        <div className="stack" style={{ gap: 10, padding: '0 10px' }}>
          {[70, 82, 64, 58].map((w) => (
            <div key={w} className="skeleton" style={{ width: `${w}%` }} />
          ))}
        </div>
      </aside>
      <main className="content">
        <div className="skeleton" style={{ width: 180, height: 24, marginBottom: 28 }} />
        <SkeletonRows rows={5} />
      </main>
    </div>
  );
}

export function AppLayout() {
  const { auth, config } = useServices();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const session = useQuery({ queryKey: ['session'], queryFn: () => auth.getSession(), staleTime: 30_000 });
  const me = useMe();
  const navigate = useNavigate();
  const redirecting = useRef(false);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    const active = me.data?.activeTenant?.id;
    if (active && tenantSelection.get() !== active) tenantSelection.set(active);
  }, [me.data?.activeTenant?.id]);

  const signedOut = session.isSuccess && !session.data;
  // Redirect once from an effect: navigating during render restarts the navigation on every
  // render and can lock the router in a loop (seen with the async Cognito session).
  useEffect(() => {
    // The API client may already have sent us to /login (401); never stack a second redirect.
    if (!signedOut || redirecting.current || location.pathname.startsWith('/login')) return;
    redirecting.current = true;
    void navigate({ to: '/login', search: { returnTo: location.href }, replace: true });
  }, [signedOut, navigate, location.href, location.pathname]);

  if (signedOut) return <ShellSkeleton />;
  if (me.isPending) return <ShellSkeleton />;
  if (me.isError) {
    return (
      <main className="content">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </main>
    );
  }
  if (!me.data.activeTenant && !me.data.isPlatformAdmin) return <NoAccess me={me.data} />;

  return (
    <MeCtx.Provider value={me.data}>
      <div className="drawer-overlay" data-open={menuOpen} onClick={() => setMenuOpen(false)} />
      <div className="shell">
        <Sidebar me={me.data} open={menuOpen} environment={config.environment} />
        <div className="main">
          <div className="mobile-bar">
            <Link to="/" className="wordmark" aria-label="Agents by Stratika, inicio">
              <strong>
                Agents<span>.</span>
              </strong>
            </Link>
            <Button
              variant="ghost"
              iconOnly
              icon={menuOpen ? 'x' : 'menu'}
              aria-expanded={menuOpen}
              aria-controls="main-nav"
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
              onClick={() => setMenuOpen((v) => !v)}
            />
          </div>
          <main className="content" id="contenido">
            <Outlet />
          </main>
        </div>
      </div>
    </MeCtx.Provider>
  );
}
